import React, { useState } from 'react'
import { useEffectOnce } from 'react-use'
import { useDispatch, useSelector } from 'react-redux'
import { Skeleton, message } from 'antd'

import { AppContent } from '../../components/utils/utils'
import { User } from '../../reducers/userReducer'
import { Organization } from '../../reducers/organizationReducer'
import { useAxiosWithHeader } from '../../utils'
import { SettingsNotOnboarded } from '../../components/SettingsNotOnboarded'
import { setIsOnboarded, PlayerState } from '../../reducers/playersReducer'
import getOrgAndEngineers from '../../api/getOrgAndEngineers'
import { isSlackAuth } from '../../components/TeamChannels/helpers'
import {
  fetchRepositoriesForOrg,
  isRepoActionable,
  toggleRepoOpeningPR,
} from '../../api/repositories'
import RepositoriesList from '../../components/RepositoriesList'

function RepositoriesSettings() {
  const user = useSelector(User.selectors.selectUser)
  const organization = useSelector(Organization.selectors.getOrganization)
  const isOnboarded = useSelector(PlayerState.selectors.isOnboarded)
  const dispatch = useDispatch()
  const axios = useAxiosWithHeader()
  const [loading, setLoading] = useState(true)

  const slackAuth = isSlackAuth({ user, organization })

  useEffectOnce(() => {
    ;(async () => {
      if (user) {
        await new Promise((resolve) => setTimeout(resolve, 500))
        getOrgAndEngineers({
          organization,
          slackAuth,
          dispatch,
          user,
          axios,
          setIsOnboarded,
        })
      }
      const orgId =
        organization?.id || user?.organizations?.[user.organizations.length - 1]?.id
      if (!orgId) {
        setLoading(false)
        return
      }
      try {
        await fetchRepositoriesForOrg({ axios, orgId, dispatch })
      } catch (error) {
        console.log('Error fetching repositories', error)
        message.error("Couldn't load repositories, try again or contact support.")
      } finally {
        setLoading(false)
      }
    })()
  })

  const onToggle = async (repo, value) => {
    await toggleRepoOpeningPR({
      axios,
      repoId: repo.id,
      openingPR: value,
      dispatch,
    })
  }

  const onBulkToggle = async (value) => {
    const repositories = organization?.repositories || []
    const targets = repositories.filter(
      (repo) => repo.openingPR !== value && isRepoActionable(repo),
    )
    if (targets.length === 0) return
    const hide = message.loading(
      `${value ? 'Activating' : 'Deactivating'} ${targets.length} repositories...`,
      0,
    )
    try {
      await Promise.all(
        targets.map((repo) =>
          toggleRepoOpeningPR({
            axios,
            repoId: repo.id,
            openingPR: value,
            dispatch,
          }),
        ),
      )
      message.success(
        `Updated ${targets.length} repositor${targets.length === 1 ? 'y' : 'ies'}.`,
      )
    } catch (error) {
      console.log('Error in bulk toggle', error)
      message.error('Some repositories could not be updated.')
    } finally {
      hide()
    }
  }

  return (
    <div>
      <AppContent className="mt-4 flex">
        {!isOnboarded && <SettingsNotOnboarded organization={organization} />}
        {isOnboarded && (
          <div className="w-full">
            <div className="mb-4">
              <h2 className="font-semibold pb-0 mb-1 text-secondaryBlack">
                Repositories
              </h2>
              <p className="text-gray-600">
                Pick which repositories Axolo should react to. When a repository is
                disabled, Axolo stops creating new PR channels and stops reacting to
                events for it. Existing channels remain unchanged.
              </p>
            </div>
            {loading ? (
              <Skeleton active paragraph={{ rows: 6 }} />
            ) : (
              <RepositoriesList
                repositories={organization?.repositories || []}
                onToggle={onToggle}
                onBulkToggle={onBulkToggle}
              />
            )}
          </div>
        )}
      </AppContent>
    </div>
  )
}

export default RepositoriesSettings
